import './PublicPsychologist.css';
import { MapPin, BriefcaseBusiness, ShieldCheck, Copy, Share2 } from 'lucide-react';
import { PublicProfileSections } from '../components/PublicProfileDetails';
import React, { useEffect, useState, useContext } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';

export default function PublicPsychologist() {
  const { slug } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [profile, setProfile] = useState(() => {
    try {const seed = JSON.parse(document.getElementById('insight-profile-data')?.textContent || 'null'); return seed?.slug === slug ? seed : null;} catch {return null;}
  });
  const [shareMessage, setShareMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);
  useEffect(() => {
    let active = true;
    setProfile(previous => previous?.slug === slug ? previous : null); setError(''); setPhotoFailed(false);
    axios.get(`${API}/public/psychologists/${encodeURIComponent(slug)}`)
      .then(res => { if (active) setProfile(res.data); })
      .catch(() => { if (active) {setProfile(null); setError('This psychologist page is not available.');} });
    return () => { active = false; };
  }, [slug]);
  useEffect(() => {
    const owned = [];
    const set = (tag, attrs) => {
      const node = document.createElement(tag);
      Object.entries(attrs).forEach(([key,value]) => node.setAttribute(key,value));
      document.head.appendChild(node); owned.push(node); return node;
    };
    document.querySelectorAll('link[rel="canonical"],meta[name="robots"],meta[name="description"],meta[property^="og:"],#profile-structured-data').forEach(node => node.remove());
    set('meta',{name:'robots',content:profile?.indexable === true ? 'index, follow' : 'noindex, follow'});
    if (profile) {
      const canonical = `https://insightanalysis.in/p/${encodeURIComponent(slug)}`;
      const title = `${profile.name || 'Psychologist'} | Psychologist | INSIGHT`;
      const description = (profile.bio || profile.qualifications || 'Public professional profile on INSIGHT.').replace(/\s+/g,' ').trim().slice(0,160);
      document.title = title;
      set('meta',{name:'description',content:description});
      set('link',{rel:'canonical',href:canonical});
      Object.entries({'og:title':title,'og:description':description,'og:type':'profile','og:url':canonical,'og:site_name':'INSIGHT'}).forEach(([property,content]) => set('meta',{property,content}));
      const person = {'@type':'Person','@id':canonical+'#person',name:profile.name,url:canonical,jobTitle:'Psychologist'};
      if (profile.bio) person.description = profile.bio;
      if (!profile.demo && profile.qualifications) person.hasCredential = {'@type':'EducationalOccupationalCredential',name:profile.qualifications};
      if (profile.photo_url) set('meta',{property:'og:image',content:`https://insightanalysis.in/api${profile.photo_url}`});
      set('script',{type:'application/ld+json',id:'profile-structured-data'}).textContent = JSON.stringify({'@context':'https://schema.org','@type':'ProfilePage','@id':canonical+'#profile',url:canonical,mainEntity:person});
    } else document.title = error ? 'Page not found | INSIGHT' : 'Professional Profile | INSIGHT';
    return () => owned.forEach(node => node.remove());
  }, [profile, slug, error]);
  const follow = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/client/referral`, { referral_token: profile.referral_token });
      navigate('/client-dashboard');
    } catch (err) { setError(err.response?.data?.detail || 'Unable to continue. Please reopen this page.'); }
    finally { setBusy(false); }
  };
  return <main className="professional-profile">
    <div className="profile-portfolio">
      {error ? <><p role="alert" className="my-6">{error}</p><Link to="/">Back to INSIGHT</Link></> : !profile ? <p>Loading psychologist page…</p> : <>
        <div className="profile-header flex flex-col sm:flex-row sm:items-center gap-5 mb-5">
          {profile.photo_url && !photoFailed ? (
            <img
              src={`${API}${profile.photo_url}`}
              alt={`${profile.name} profile`}
              onError={() => setPhotoFailed(true)}
              className="h-24 w-24 shrink-0 rounded-full object-cover border border-slate-200"
              data-testid="psychologist-profile-photo"
            />
          ) : (
            <div
              role="img"
              aria-label={`${profile.name} profile photo unavailable`}
              className="h-24 w-24 shrink-0 rounded-full border border-slate-200 bg-slate-100 flex items-center justify-center text-2xl font-semibold text-slate-500"
              data-testid="psychologist-profile-photo-placeholder"
            >
              {profile.name.trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase()}
            </div>
          )}
          <div className="profile-identity min-w-0">
            <p className="profile-designation">{profile.public_details?.designation || 'Psychologist'}</p>
            {profile.demo && <p className="font-semibold text-amber-700">Demonstration · Fictional professional profile</p>}
            {!profile.demo && profile.professional_verified === true && <p className="profile-verified"><ShieldCheck size={18} aria-hidden="true" />Professional credentials verified</p>}
            <h1 className="text-3xl font-semibold mb-3 break-words">{profile.name}</h1>
            <p className="profile-qualification">{profile.qualifications}</p>
        {profile.specialization && <div className="profile-specializations"><h2>Specializations</h2><div className="profile-tags">{[...new Set(profile.specialization.split(',').map(v=>v.trim()).filter(Boolean))].map(v=><span key={v}>{v}</span>)}</div></div>}
<div className="profile-metadata">        {profile.experience_years > 0 && <span><BriefcaseBusiness size={19} aria-hidden="true" />{profile.experience_years} years of experience</span>}
        {(profile.public_details?.city || profile.public_details?.state) && <span><MapPin size={19} aria-hidden="true" />{[profile.public_details.city, profile.public_details.state].filter(Boolean).join(', ')}</span>}
</div>
{profile.public_details?.consultation_modes?.length>0 && <div className="profile-mode-chips" aria-label="Consultation modes">{profile.public_details.consultation_modes.map(mode=><span key={mode}>{mode}</span>)}</div>}
          </div>
        </div>
        
        {profile.bio && <section className="profile-about"><h2 className="text-xl font-semibold mb-3">About the Professional</h2><p className="whitespace-pre-wrap break-words text-slate-700">{profile.bio}</p></section>}
        <PublicProfileSections details={profile.public_details} qualifications={profile.qualifications || ''} />
        <div className="profile-share-actions">
          <Button variant="outline" onClick={async()=>{try {await navigator.clipboard.writeText(`https://insightanalysis.in/p/${encodeURIComponent(slug)}`);setShareMessage('Profile link copied.');} catch {setShareMessage('Copy the profile URL from your browser address bar.');}}}><Copy size={18} aria-hidden="true" />Copy profile link</Button>
          <Button variant="outline" onClick={async()=>{try {if (typeof navigator.share !== 'function') {await navigator.clipboard.writeText(`https://insightanalysis.in/p/${encodeURIComponent(slug)}`);setShareMessage('Profile link copied. Ready to share.');return;} await navigator.share({title:profile.name,url:`https://insightanalysis.in/p/${encodeURIComponent(slug)}`});} catch (e) {if(e.name!=='AbortError') setShareMessage('Unable to share. Use Copy profile link.');}}}><Share2 size={18} aria-hidden="true" />Share profile</Button>
          <span role="status" className="text-sm">{shareMessage}</span>
        </div>
        <div className="profile-client-panel"><h2>Continue with {profile.name}</h2><p className="profile-footer-note">Continue through this page to work with {profile.name} for future activity. Your previous assessments and appointments stay with their original psychologist.</p>
        {!user ? <div className="profile-client-actions">
          <Button asChild><Link to={`/register?referral=${encodeURIComponent(profile.referral_token)}`}>Register as Client</Link></Button>
          <Button variant="outline" asChild><Link to={`/login?referral=${encodeURIComponent(profile.referral_token)}`}>Client Login</Link></Button>
        </div> : user.role === 'client' ? <Button onClick={follow} disabled={busy}>{busy ? 'Continuing…' : `Continue with ${profile.name}`}</Button> : <p>Please sign out and use a client account to continue.</p>}
        </div>
      </>}
    </div>
  </main>;
}
